import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, map, distinctUntilChanged, take } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { FetchAllCustomFields } from 'src/app/features/settings/store/settings.actions';
import { ICustomField } from 'src/app/features/settings/models/settings.models';
import {
  CustomFieldTypes,
  CustomFieldVisibilityTypes,
  RelatedObjectsTypes,
} from 'src/app/features/settings/models/custom-fields.constants';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/config/language.service';
import { CustomFieldDisplayService, ICustomFieldValues } from 'src/app/shared/custom-field-display.service';

/** Prefix that namespaces a custom-field column key against the hardcoded ones. */
export const PRODUCT_CUSTOM_FIELD_COLUMN_PREFIX = 'cf:';

/** Column key shape for a product custom field, e.g. `cf:certification_code`. */
export type ProductCustomFieldColumnKey = `cf:${string}`;

export interface IProductCustomFieldColumn {
  key: ProductCustomFieldColumnKey;
  /** Already-resolved label (definitions carry a per-language label map, not an i18n key). */
  label: string;
  definition: ICustomField;
}

/** Everything a custom field table cell needs, resolved once per render. */
export interface IProductCustomFieldCell {
  kind: 'chips' | 'link' | 'text';
  chips: string[];
  href: string;
  /** Display text, already translated where the field type calls for it. */
  text: string;
}

/**
 * Exposes product-level (`inventory`) custom field definitions as optional
 * column descriptors for the order-lines tables (WB-7619).
 *
 * Definitions already reach the frontend through the settings store; the
 * per-line *values* arrive on `IParseProduct.custom_fields` once WB-7644 ships.
 * Until then the columns render empty, mirroring how WB-7463 shipped the
 * order-company custom field block ahead of its backend.
 */
@Injectable({ providedIn: 'root' })
export class ProductCustomFieldColumnsService {
  constructor(
    private store: Store<AppState>,
    private languageService: LanguageService,
    private display: CustomFieldDisplayService,
    private translate: TranslateService,
  ) { }

  /**
   * Backoffice-visible product custom fields, in definition order. Emits `[]`
   * until the definitions land, so hosts can hydrate columns twice.
   */
  columns$(): Observable<IProductCustomFieldColumn[]> {
    this.ensureLoaded();
    const language = this.languageService.getCurrentLanguage();
    return this.store.select((state) => state.settings.customFields).pipe(
      map((definitions) => (definitions ?? []).filter((definition) => this.isOrderLineColumn(definition))),
      distinctUntilChanged((previous, current) => this.signature(previous) === this.signature(current)),
      map((definitions) => definitions.map((definition) => ({
        key: this.columnKey(definition),
        label: this.display.label(definition, language),
        definition,
      }))),
    );
  }

  /**
   * Resolve one table cell for `column` from a line's `custom_fields` bag.
   * Shared by the order details table and the side-by-side panel so both render
   * a value the same way.
   */
  cell(column: IProductCustomFieldColumn, values: ICustomFieldValues | null | undefined): IProductCustomFieldCell {
    const definition = column.definition;
    const empty: IProductCustomFieldCell = {
      kind: 'text',
      chips: [],
      href: '',
      text: CustomFieldDisplayService.EMPTY_PLACEHOLDER,
    };

    if (this.display.isSelect(definition)) {
      const chips = this.display.selectValues(values, definition);
      return chips.length ? { kind: 'chips', chips, href: '', text: '' } : empty;
    }

    const text = this.display.text(values, definition);
    if (text === CustomFieldDisplayService.EMPTY_PLACEHOLDER) return empty;

    const href = this.display.href(values, definition);
    if (href && (this.display.isEmail(definition) || this.display.isUrl(definition))) {
      return { kind: 'link', chips: [], href, text };
    }

    // Booleans come back as an i18n key ('common.yes' / 'common.no'); every
    // other type is literal user data and must never hit the translator.
    const isBoolean = definition.field_type === CustomFieldTypes.BOOLEAN;
    return { kind: 'text', chips: [], href: '', text: isBoolean ? this.translate.instant(text) : text };
  }

  columnKey(definition: ICustomField): ProductCustomFieldColumnKey {
    return `${PRODUCT_CUSTOM_FIELD_COLUMN_PREFIX}${definition.technical_name}`;
  }

  /** Fetch definitions once per session — the effect always refetches, so guard on an empty store. */
  private ensureLoaded(): void {
    this.store.select((state) => state.settings.customFields).pipe(take(1)).subscribe((definitions) => {
      if (!definitions || definitions.length === 0) {
        this.store.dispatch(FetchAllCustomFields());
      }
    });
  }

  private isOrderLineColumn(definition: ICustomField): boolean {
    if (definition.object_type !== RelatedObjectsTypes.INVENTORY) return false;
    const visibility = definition.visibility ?? [];
    return (
      visibility.length === 0 ||
      visibility.includes(CustomFieldVisibilityTypes.ALL) ||
      visibility.includes(CustomFieldVisibilityTypes.BACKOFFICE)
    );
  }

  /** Identity of a definition set for change detection — id + technical name is enough. */
  private signature(definitions: ICustomField[]): string {
    return definitions.map((definition) => `${definition.id}:${definition.technical_name}`).join('|');
  }
}
